import { createAsyncThunk } from "@reduxjs/toolkit";
import Sessions from "utils/Sessions";
import { Common } from "utils/Constants";
import configThunk from "redux/Thunks/Config";
import ApiService, { fetchAPIService } from "../ApiService/ApiService";

// A 403 from the Snag API means "no Snag access" (e.g. the permission was removed after login),
// not an expired session - keep the user logged in and let the screen show the message.
const snagRequestConfig = () => ({
  headers: { Authorization: `Bearer ${Sessions.userToken}` },
  keepSessionOn403: true,
});

// Screen permissions are loaded at login - refresh them so the menu / page reflect the change
const refreshPermissionsOn403 = (res, dispatch) => {
  if (res?.status === Common.API_STATUS_403) {
    dispatch(configThunk());
  }
  return res;
};

export const getSnags = createAsyncThunk("snags/list", async (params, { dispatch }) => {
  const res = await ApiService.get(`snags?${params}`, snagRequestConfig())
    .then((r) => r)
    .catch((error) => error.response);
  return refreshPermissionsOn403(res, dispatch);
});

export const getSnagById = createAsyncThunk("snags/get", async (id, { dispatch }) => {
  const res = await ApiService.get(`snags/${id}`, snagRequestConfig())
    .then((r) => r)
    .catch((error) => error.response);
  return refreshPermissionsOn403(res, dispatch);
});

export const getProjectMapSnags = createAsyncThunk("snags/map-view", async (params) => {
  const projectId = typeof params === "object" ? params?.projectId : params;
  const res = await ApiService.get(`snags/project/${projectId}/map-view`, {
    ...snagRequestConfig(),
    signal: params?.signal,
  })
    .then((r) => r)
    .catch((error) => error.response);
  return res;
});

export const createSnag = createAsyncThunk("snags/create", async (body, { dispatch }) => {
  const res = await ApiService.post(`snags`, { ...body }, snagRequestConfig())
    .then((r) => r)
    .catch((err) => err.response);
  return refreshPermissionsOn403(res, dispatch);
});

export const updateSnag = createAsyncThunk("snags/update", async (body, { dispatch }) => {
  const res = await ApiService.patch(`snags/${body.id}`, { ...body.body }, snagRequestConfig())
    .then((r) => r)
    .catch((err) => err.response);
  return refreshPermissionsOn403(res, dispatch);
});

export const deleteSnag = createAsyncThunk("snags/delete", async (id, { dispatch }) => {
  const res = await ApiService.delete(`snags/${id}`, snagRequestConfig())
    .then((r) => r)
    .catch((err) => err.response);
  return refreshPermissionsOn403(res, dispatch);
});

// Returns the exported file as a Blob, `{ status, message }` when access was denied,
// or null when the export failed
export const exportSnags = createAsyncThunk("snags/export", async (params, { dispatch }) => {
  try {
    const response = await fetchAPIService(`/snags/export?${params}`, {
      method: "GET",
      keepSessionOn403: true,
    });
    if (response.status === Common.API_STATUS_403) {
      const data = await response.json().catch(() => ({}));
      dispatch(configThunk());
      return { status: Common.API_STATUS_403, message: data?.message };
    }
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return null;
    }
    return await response.blob();
  } catch (error) {
    return null;
  }
});

export default getSnags;
